
import { useState } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription,
  CardFooter
} from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { FairClickSite } from '@/types/fairclick';
import { AddCreditsForm } from './AddCreditsForm';
import { Trash2, BarChart, Calendar, ExternalLink } from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface SitesListProps {
  sites: FairClickSite[];
  userCredits: number;
  onAddCredits: (siteId: string, credits: number) => Promise<boolean>;
  onDeleteSite: (siteId: string) => Promise<boolean>;
}

export const SitesList = ({ sites, userCredits, onAddCredits, onDeleteSite }: SitesListProps) => {
  const [deletingSite, setDeletingSite] = useState<string | null>(null);
  
  const handleDeleteSite = async (siteId: string) => {
    setDeletingSite(siteId);
    try {
      await onDeleteSite(siteId);
    } finally {
      setDeletingSite(null);
    }
  };
  
  if (sites.length === 0) {
    return (
      <Card className="w-full">
        <CardContent className="py-6 text-center">
          <p className="text-gray-500">
            Vous n'avez pas encore ajouté de sites à promouvoir.
          </p>
        </CardContent>
      </Card>
    );
  }
  
  return (
    <div className="space-y-4">
      {sites.map(site => (
        <Card key={site.id} className="w-full">
          <CardHeader className="pb-2">
            <div className="flex justify-between">
              <div>
                <CardTitle className="text-lg">{site.title}</CardTitle>
                <a 
                  href={site.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-500 hover:underline flex items-center gap-1"
                >
                  {site.url} <ExternalLink size={14} />
                </a>
              </div>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="text-red-500 hover:text-red-700 hover:bg-red-50"
                  >
                    <Trash2 size={18} />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Supprimer ce site ?</AlertDialogTitle>
                    <AlertDialogDescription>
                      Cette action est irréversible. Les crédits restants ({site.credits}) seront remboursés à votre compte.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Annuler</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={() => handleDeleteSite(site.id)}
                      className="bg-red-500 hover:bg-red-600"
                    >
                      {deletingSite === site.id ? "Suppression..." : "Supprimer"}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </div>
          </CardHeader>
          <CardContent className="pb-2">
            {site.description && (
              <CardDescription className="mt-1 mb-2">
                {site.description}
              </CardDescription>
            )}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-4">
              <div className="flex flex-col items-center p-2 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-500 flex items-center gap-1">
                  <Calendar size={14} /> Ajouté le
                </span>
                <span className="font-medium">
                  {format(new Date(site.created_at), 'dd MMM yyyy', { locale: fr })}
                </span>
              </div>
              <div className="flex flex-col items-center p-2 bg-gray-50 rounded-lg">
                <span className="text-sm text-gray-500 flex items-center gap-1">
                  <BarChart size={14} /> Visites reçues
                </span>
                <span className="font-medium">{site.total_visits}</span>
              </div>
              <div className="flex flex-col items-center p-2 bg-green-50 rounded-lg">
                <span className="text-sm text-green-600">Crédits restants</span>
                <span className="font-medium text-green-700">{site.credits}</span>
              </div>
            </div>
          </CardContent>
          <CardFooter>
            <AddCreditsForm 
              site={site} 
              userCredits={userCredits} 
              onAddCredits={onAddCredits} 
            />
          </CardFooter>
        </Card>
      ))}
    </div>
  );
};
